import React from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { FamilyProvider } from "@/contexts/FamilyContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ChatProvider } from "@/contexts/ChatContext";
import { NotificationPrompt } from "@/components/notifications";
import { VideoCall } from "@/components/chat";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { MainLayout } from "@/components/layout";
import { 
  HomePage, 
  LoginPage, 
  RegisterPage, 
  ForgotPasswordPage,
  DoctorsPage,
  DoctorDetailPage,
  ClinicsPage,
  SpecialtiesPage,
  AppointmentsPage,
  AppointmentDetailsPage,
  MedicalRecordsPage,
  ProfilePage,
  FavoritesPage,
  NotificationsPage,
  BookingCheckoutPage,
  RateDoctorPage,
  MessagesPage,
  WalletPage,
  SettingsPage,
  HelpPage,
  ClinicDetailPage,
  FamilyMembersPage,
  AboutPage,
  ContactPage,
  PrivacyPage,
  TermsPage
} from "@/pages";
import {
  DoctorDashboard,
  DoctorAppointments,
  DoctorSchedule,
  DoctorPatients,
  DoctorReviews,
  PatientDetailPage,
  DoctorConsultationEditPage,
  DoctorProfile,
  DoctorWallet
} from "@/pages/doctor";
import {
  ClinicDashboard,
  ClinicPatients,
  ClinicStaff,
  ClinicBilling,
  ClinicReports,
  ClinicSettings
} from "@/pages/clinic";
import {
  PharmacyListPage,
  MedicinesPage,
  PharmacyCartPage,
  PharmacyCheckoutPage,
  PharmacyOrdersPage,
  MedicineDetailPage
} from "@/pages/pharmacy";
import { PharmacyCartProvider } from "@/contexts/PharmacyCartContext";
import { Toaster } from "@/components/ui/sonner";

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FavoritesProvider>
          <NotificationProvider>
          <ChatProvider>
          <FamilyProvider>
            <PharmacyCartProvider>
              <BrowserRouter>
              <Routes>
              {/* Public routes with MainLayout */}
              <Route element={<MainLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/doctors" element={<DoctorsPage />} />
                <Route path="/doctors/:id" element={<DoctorDetailPage />} />
                <Route path="/doctors/:id/book" element={<DoctorDetailPage />} />
                <Route path="/clinics" element={<ClinicsPage />} />
                <Route path="/specialties" element={<SpecialtiesPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                
                {/* Patient Features */}
                <Route path="/favorites" element={<FavoritesPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/checkout/:doctorId" element={<BookingCheckoutPage />} />
                <Route path="/rate/:doctorId" element={<RateDoctorPage />} />
                <Route path="/family" element={<FamilyMembersPage />} />
                
                {/* Protected routes (auth handled inside components) */}
                <Route path="/appointments" element={<ProtectedRoute><AppointmentsPage /></ProtectedRoute>} />
                <Route path="/appointments/:id" element={<ProtectedRoute><AppointmentDetailsPage /></ProtectedRoute>} />
                <Route path="/medical-records" element={<ProtectedRoute><MedicalRecordsPage /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
                <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
                <Route path="/clinics/:id" element={<ClinicDetailPage />} />
                
                {/* Doctor Portal Routes */}
                <Route path="/doctor/dashboard" element={<ProtectedRoute allowedUserTypes={['doctor']}><DoctorDashboard /></ProtectedRoute>} />
                <Route path="/doctor/appointments" element={<ProtectedRoute allowedUserTypes={['doctor']}><DoctorAppointments /></ProtectedRoute>} />
                <Route path="/doctor/appointments/:id" element={<ProtectedRoute allowedUserTypes={['doctor']}><AppointmentDetailsPage /></ProtectedRoute>} />
                <Route path="/doctor/schedule" element={<ProtectedRoute allowedUserTypes={['doctor']}><DoctorSchedule /></ProtectedRoute>} />
                <Route path="/doctor/availability" element={<ProtectedRoute allowedUserTypes={['doctor']}><DoctorSchedule /></ProtectedRoute>} />
                <Route path="/doctor/services" element={<ProtectedRoute allowedUserTypes={['doctor']}><DoctorSchedule /></ProtectedRoute>} />
                <Route path="/doctor/patients" element={<ProtectedRoute allowedUserTypes={['doctor']}><DoctorPatients /></ProtectedRoute>} />
                <Route path="/doctor/patients/:id" element={<ProtectedRoute allowedUserTypes={['doctor']}><PatientDetailPage /></ProtectedRoute>} />
                <Route path="/doctor/consultations/:consultationId/edit" element={<ProtectedRoute allowedUserTypes={['doctor']}><DoctorConsultationEditPage /></ProtectedRoute>} />
                <Route path="/doctor/reviews" element={<ProtectedRoute allowedUserTypes={['doctor']}><DoctorReviews /></ProtectedRoute>} />
                <Route path="/doctor/profile" element={<ProtectedRoute allowedUserTypes={['doctor']}><DoctorProfile /></ProtectedRoute>} />
                <Route path="/doctor/wallet" element={<ProtectedRoute allowedUserTypes={['doctor']}><DoctorWallet /></ProtectedRoute>} />
                
                {/* Clinic Management Dashboard Routes */}
                <Route path="/clinic/dashboard" element={<ProtectedRoute allowedUserTypes={['clinic']}><ClinicDashboard /></ProtectedRoute>} />
                <Route path="/clinic/patients" element={<ProtectedRoute allowedUserTypes={['clinic']}><ClinicPatients /></ProtectedRoute>} />
                <Route path="/clinic/patients/:id" element={<ProtectedRoute allowedUserTypes={['clinic']}><ClinicPatients /></ProtectedRoute>} />
                <Route path="/clinic/staff" element={<ProtectedRoute allowedUserTypes={['clinic']}><ClinicStaff /></ProtectedRoute>} />
                <Route path="/clinic/billing" element={<ProtectedRoute allowedUserTypes={['clinic']}><ClinicBilling /></ProtectedRoute>} />
                <Route path="/clinic/reports" element={<ProtectedRoute allowedUserTypes={['clinic']}><ClinicReports /></ProtectedRoute>} />
                <Route path="/clinic/settings" element={<ProtectedRoute allowedUserTypes={['clinic']}><ClinicSettings /></ProtectedRoute>} />
                
                {/* Pharmacy Routes */}
                <Route path="/pharmacy" element={<PharmacyListPage />} />
                <Route path="/pharmacy/medicines" element={<MedicinesPage />} />
                <Route path="/pharmacy/:id" element={<MedicinesPage />} />
                <Route path="/pharmacy/medicine/:id" element={<MedicineDetailPage />} />
                <Route path="/pharmacy/cart" element={<PharmacyCartPage />} />
                <Route path="/pharmacy/checkout" element={<PharmacyCheckoutPage />} />
                <Route path="/pharmacy/orders" element={<ProtectedRoute><PharmacyOrdersPage /></ProtectedRoute>} />
              </Route>
              
              {/* Auth routes without MainLayout */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            </Routes>
            <NotificationPrompt />
            <VideoCall />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
          </PharmacyCartProvider>
        </FamilyProvider>
        </ChatProvider>
      </NotificationProvider>
      </FavoritesProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
